import * as React from 'react';
import {RouteChildrenProps} from 'react-router-dom';
import {useTranslation, Trans} from 'react-i18next';
import styled from '@emotion/styled';

import {vr4} from '>styles/mixins/verticalRhythm';
import LanguageSelector from '>components/language/languageSelector';
import {Section} from '>shared/components/heading/heading';
import {SectionHeading, SectionSubHeadingBold} from '>shared/components/typography/baseTypography2';

import {
  BodyContent,
  Content,
  ContentContainer,
  ContentSection,
  HeadingSection,
  LargeLI,
  PageContainer
} from '../components/styles/policy.styles';

const SectionSubHeading = styled(SectionSubHeadingBold)`
  font-size: 1.8rem;
  ${vr4}
`;

const UnderlinedSubHeading = styled(SectionSubHeading)`
  text-decoration: underline;
`;

const ItalicSubHeading = styled(SectionSubHeading)`
  font-style: italic;
`;

const ItalicContent = styled(Content)`
  font-style: italic;
`;

const WonderlicContactList = styled.ul`
  list-style-type: none;
  li {
    margin-bottom: 2.4rem;
  }
`;

export const PrivacyPage: React.FC<RouteChildrenProps> = () => {
  const {t} = useTranslation('privacy');

  return (
    <PageContainer role="main">
      <Trans>
        <ContentContainer>
          <HeadingSection>
            <SectionHeading>{t('privacy:pageTitle')}</SectionHeading>
            <BodyContent>
              {t('privacy:lastUpdated', {date: new Date('August 30 2023')})}
              <LanguageSelector/>
            </BodyContent>
          </HeadingSection>
          <Section>
            <ContentSection role="region" aria-label="introduction">
              <Content>
                {t('privacy:firstParagraph')}
              </Content>
              <Content>
                {t('privacy:secondParagraph')}
              </Content>
            </ContentSection>
            <ContentSection role="region" aria-labelledby="definitions">
              <UnderlinedSubHeading id="definitions">{t('privacy:definitions.title')}</UnderlinedSubHeading>
              <Content>
                {t('privacy:definitions.firstParagraph')}
              </Content>
              <Content>
                {t('privacy:definitions.account')}
              </Content>
              <Content>
                {t('privacy:definitions.company')}
              </Content>
              <Content>{t('privacy:definitions.companyGdpr')}</Content>
              <Content>
                {t('privacy:definitions.country')}
              </Content>
              <Content>
                {t('privacy:definitions.service')}
              </Content>
              <Content>
                <Trans 
                  i18nKey='privacy:definitions.website'
                  components={{
                    selectLink: <a href="https://select.wonderlic.com/"/>,
                    developLink: <a href="https://develop.wonderlic.com/"/>,
                    woLink: <a href="https://www.wonderliconline.com/"/>
                  }}>
                </Trans>
              </Content>
              <Content>
                {t('privacy:definitions.you')}
              </Content>
            </ContentSection>
            <ContentSection role="region" aria-labelledby="collectingData">
              <UnderlinedSubHeading id="collectingData">
                {t('privacy:collecting.title')}
              </UnderlinedSubHeading>
              <ItalicSubHeading>{t('privacy:collecting.firstSectionHeading')}</ItalicSubHeading>
              <SectionSubHeadingBold>{t('privacy:collecting.personalData.heading')}</SectionSubHeadingBold>
              <Content>
                <Trans 
                  i18nKey='privacy:collecting.personalData.firstParagraph'
                  components={{
                    cookiePolicyLink: <a href="/cookie-policy" target="_blank"/>
                  }}>
                </Trans>
              </Content>
              <ItalicContent>{t('privacy:collecting.identifiers.heading')}</ItalicContent>
              <Content>
                {t('privacy:collecting.identifiers.firstParagraph')}
              </Content>
              <ItalicContent>
                {t('privacy:collecting.identifiers.secondParagraph')}
              </ItalicContent>
              <Content>
                {t('privacy:collecting.identifiers.thirdParagraph')}
              </Content>
              <ItalicContent>{t('privacy:collecting.commercialInformation.heading')}</ItalicContent>
              <Content>
                {t('privacy:collecting.commercialInformation.firstParagraph')}
              </Content>
              <ItalicContent>{t('privacy:collecting.internet.heading')}</ItalicContent>
              <Content>
                {t('privacy:collecting.internet.firstParagraph')}
              </Content>
              <Content>
                {t('privacy:collecting.internet.secondParagraph')}
              </Content>
              <Content>
                {t('privacy:collecting.internet.thirdParagraph')}
              </Content>
              <Content>
                {t('privacy:collecting.internet.fourthParagraph')}
              </Content>
              <ItalicSubHeading>{t('privacy:collecting.usePersonalData.heading')}</ItalicSubHeading>
              <Content>{t('privacy:collecting.usePersonalData.firstParagraph')}</Content>
              <Content>
                {t('privacy:collecting.usePersonalData.secondParagraph')}
              </Content>
              <Content>
                {t('privacy:collecting.usePersonalData.thirdParagraph')}
              </Content>
              <Content>
                {t('privacy:collecting.usePersonalData.fourthParagraph')}
              </Content>
              <Content>
                {t('privacy:collecting.usePersonalData.fifthParagraph')}
              </Content>
              <Content>
                {t('privacy:collecting.usePersonalData.sixthParagraph')}
              </Content>
              <Content>
                {t('privacy:collecting.usePersonalData.seventhParagraph')}
              </Content>
              <Content>
                {t('privacy:collecting.usePersonalData.eighthParagraph')}
              </Content>
              <Content>
                {t('privacy:collecting.usePersonalData.ninthParagraph')}
              </Content>
              <Content>
                {t('privacy:collecting.usePersonalData.tenthParagraph')}
              </Content>
              <ItalicSubHeading>{t('privacy:collecting.sharePersonalInformation.heading')}</ItalicSubHeading>
              <Content>
                {t('privacy:collecting.sharePersonalInformation.firstParagraph')}
              </Content>
              <ul>
                <LargeLI>
                  <Content>
                    {t('privacy:collecting.sharePersonalInformation.firstBullet')}
                  </Content>
                </LargeLI>
                <LargeLI>
                  <Content>
                    {t('privacy:collecting.sharePersonalInformation.secondBullet')}
                  </Content>
                </LargeLI>
                <LargeLI>
                  <Content>
                    {t('privacy:collecting.sharePersonalInformation.thirdBullet')}
                  </Content>
                </LargeLI>
                <LargeLI>
                  <Content>
                    {t('privacy:collecting.sharePersonalInformation.fourthBullet')}
                  </Content>
                </LargeLI>
                <LargeLI>
                  <Content>
                    {t('privacy:collecting.sharePersonalInformation.fifthBullet')}
                  </Content>
                </LargeLI>
                <LargeLI>
                  <Content>
                    {t('privacy:collecting.sharePersonalInformation.sixthBullet')}
                  </Content>
                </LargeLI>
              </ul>
              <ItalicSubHeading>{t('privacy:collecting.retentionPersonalData.heading')}</ItalicSubHeading>
              <Content>
                {t('privacy:collecting.retentionPersonalData.firstParagraph')}
              </Content>
              <Content>
                {t('privacy:collecting.retentionPersonalData.secondParagraph')}
              </Content>
              <ItalicSubHeading>{t('privacy:collecting.transferPersonalData.heading')}</ItalicSubHeading>
              <Content>
                {t('privacy:collecting.transferPersonalData.firstParagraph')}
              </Content>
              <Content>
                {t('privacy:collecting.transferPersonalData.secondParagraph')}
              </Content>
              <Content>
                {t('privacy:collecting.transferPersonalData.thirdParagraph')}
              </Content>
            </ContentSection>
            <ContentSection role="region" aria-labelledby="disclosure">
              <UnderlinedSubHeading id="disclosure">
                {t('privacy:disclosure.title')}
              </UnderlinedSubHeading>
              <ItalicSubHeading>{t('privacy:disclosure.business.heading')}</ItalicSubHeading>
              <Content>
                {t('privacy:disclosure.business.firstParagraph')}
              </Content>
              <ItalicSubHeading>{t('privacy:disclosure.law.heading')}</ItalicSubHeading>
              <Content>
                {t('privacy:disclosure.law.firstParagraph')}
              </Content>
              <ItalicSubHeading>{t('privacy:disclosure.legal.heading')}</ItalicSubHeading>
              <Content>
                {t('privacy:disclosure.legal.firstParagraph')}
              </Content>
              <ul>
                <LargeLI>
                  <Content>{t('privacy:disclosure.legal.firstBullet')}</Content>
                </LargeLI>
                <LargeLI>
                  <Content>{t('privacy:disclosure.legal.secondBullet')}</Content>
                </LargeLI>
                <LargeLI>
                  <Content>
                    {t('privacy:disclosure.legal.thirdBullet')}
                  </Content>
                </LargeLI>
                <LargeLI>
                  <Content>{t('privacy:disclosure.legal.fourthBullet')}</Content>
                </LargeLI>
                <LargeLI>
                  <Content>{t('privacy:disclosure.legal.fifthBullet')}</Content>
                </LargeLI>
              </ul>
            </ContentSection>
            <ContentSection role="region" aria-labelledby="security">
              <UnderlinedSubHeading id="security">
                {t('privacy:security.title')}
              </UnderlinedSubHeading>
              <Content>
                {t('privacy:security.firstParagraph')}
              </Content>
              <Content>
                {t('privacy:security.secondParagraph')}
              </Content>
            </ContentSection>
            <ContentSection role="region" aria-labelledby="email">
              <UnderlinedSubHeading id="email">{t('privacy:emailMarketing.title')}</UnderlinedSubHeading>
              <Content>
                {t('privacy:emailMarketing.firstParagraph')}
              </Content>
              <Content>
                {t('privacy:emailMarketing.secondParagraph')}
              </Content>
            </ContentSection>
            <ContentSection role="region" aria-labelledby="payments">
              <UnderlinedSubHeading id="payments">{t('privacy:payments.title')}</UnderlinedSubHeading>
              <Content>
                {t('privacy:payments.firstParagraph')}
              </Content>
              <Content>
                {t('privacy:payments.secondParagraph')}
              </Content>
            </ContentSection>
            <ContentSection role="region" aria-labelledby="pii">
              <UnderlinedSubHeading id="pii">{t('privacy:minors.title')}</UnderlinedSubHeading>
              <Content>
                {t('privacy:minors.firstParagraph')}
              </Content>
              <Content>
                {t('privacy:minors.secondParagraph')}
              </Content>
            </ContentSection>
            <ContentSection role="region" aria-labelledby="otherWebsites">
              <UnderlinedSubHeading id="otherWebsites">{t('privacy:links.title')}</UnderlinedSubHeading>
              <Content>
                {t('privacy:links.firstParagraph')}
              </Content>
            </ContentSection>
            <ContentSection role="region" aria-labelledby="changes">
              <UnderlinedSubHeading id="changes">{t('privacy:changes.title')}</UnderlinedSubHeading>
              <Content>
                {t('privacy:changes.firstParagraph')}
              </Content>
              <Content>
                {t('privacy:changes.secondParagraph')}
              </Content>
              <Content>
                {t('privacy:changes.thirdParagraph')}
              </Content>
            </ContentSection>
            <ContentSection role="region" aria-labelledby="contactUs">
              <UnderlinedSubHeading id="contactUs">{t('privacy:contactUs.title')}</UnderlinedSubHeading>
              <WonderlicContactList>
                <li>
                  <Content>
                    {t('privacy:contactUs.firstBullet')}
                  </Content>
                </li>
                <li>
                  <Content>{t('privacy:contactUs.secondBullet')}</Content>
                </li>
                <li>
                  <Content>
                    <Trans 
                      i18nKey='privacy:contactUs.thirdBullet'
                      components={{
                        contactUsLink: <a href="https://wonderlic.com/contact-us/"/>
                      }}>
                    </Trans>
                  </Content>
                </li>
                <li>
                  <Content>{t('privacy:contactUs.fourthBullet')}</Content>
                </li>
                <li>
                  <Content>{t('privacy:contactUs.fifthBullet')}</Content>
                </li>
              </WonderlicContactList>
            </ContentSection>
            <ContentSection role="region" aria-labelledby="canadianResidents">
              <UnderlinedSubHeading id="canadianResidents">
                {t('privacy:canadianResidents.title')}
              </UnderlinedSubHeading>
              <Content>
                {t('privacy:canadianResidents.firstParagraph')}
              </Content>
            </ContentSection>
            <ContentSection role="region" aria-labelledby="europeanResidents">
              <UnderlinedSubHeading id="europeanResidents">
                {t('privacy:europeanResidents.title')}
              </UnderlinedSubHeading>
              <Content>
                {t('privacy:europeanResidents.firstParagraph')}
              </Content>
              <ItalicSubHeading>{t('privacy:europeanResidents.gdprLegal.heading')}</ItalicSubHeading>
              <Content>{t('privacy:europeanResidents.gdprLegal.firstParagraph')}</Content>
              <ul>
                <LargeLI>
                  <Content>
                    {t('privacy:europeanResidents.gdprLegal.firstBullet')}
                  </Content>
                </LargeLI>
                <LargeLI>
                  <Content>
                    {t('privacy:europeanResidents.gdprLegal.secondBullet')}
                  </Content>
                </LargeLI>
                <LargeLI>
                  <Content>
                    {t('privacy:europeanResidents.gdprLegal.thirdBullet')}
                  </Content>
                </LargeLI>
                <LargeLI>
                  <Content>
                    {t('privacy:europeanResidents.gdprLegal.fourthBullet')}
                  </Content>
                </LargeLI>
                <LargeLI>
                  <Content>
                    {t('privacy:europeanResidents.gdprLegal.fifthBullet')}
                  </Content>
                </LargeLI>
                <LargeLI>
                  <Content>
                    {t('privacy:europeanResidents.gdprLegal.sixthBullet')}
                  </Content>
                </LargeLI>
              </ul>
              <Content>
                {t('privacy:europeanResidents.secondParagraph')}
              </Content>
              <ItalicSubHeading>{t('privacy:europeanResidents.gdprRights.heading')}</ItalicSubHeading>
              <Content>
                {t('privacy:europeanResidents.gdprRights.firstParagraph')}
              </Content>
              <ul>
                <LargeLI>
                  <Content>
                    {t('privacy:europeanResidents.gdprRights.firstBullet')}
                  </Content>
                </LargeLI>
                <LargeLI>
                  <Content>
                    {t('privacy:europeanResidents.gdprRights.secondBullet')}
                  </Content>
                </LargeLI>
                <LargeLI>
                  <Content>
                    {t('privacy:europeanResidents.gdprRights.thirdBullet')}
                  </Content>
                </LargeLI>
                <LargeLI>
                  <Content>
                    {t('privacy:europeanResidents.gdprRights.fourthBullet')}
                  </Content>
                </LargeLI>
                <LargeLI>
                  <Content>
                    {t('privacy:europeanResidents.gdprRights.fifthBullet')}
                  </Content>
                </LargeLI>
                <LargeLI>
                  <Content>
                    {t('privacy:europeanResidents.gdprRights.sixthBullet')}
                  </Content>
                </LargeLI>
              </ul>
              <ItalicSubHeading>{t('privacy:europeanResidents.gdprProtection.heading')}</ItalicSubHeading>
              <Content>
                {t('privacy:europeanResidents.gdprProtection.firstParagraph')}
              </Content>
              <Content>
                {t('privacy:europeanResidents.gdprProtection.secondParagraph')}
              </Content>
            </ContentSection>
            <ContentSection role="region" aria-labelledby="californiaResidents">
              <UnderlinedSubHeading id="californiaResidents">
                {t('privacy:additionalStates.title')}
              </UnderlinedSubHeading>
              <Content>
                {t('privacy:additionalStates.firstParagraph')}
              </Content>
              <ul>
                <LargeLI>
                  <Content>
                    {t('privacy:additionalStates.firstBullet')}
                  </Content>
                </LargeLI>
                <LargeLI>
                  <Content>
                    {t('privacy:additionalStates.secondBullet.paragraph')}
                  </Content>
                  <ul>
                    <LargeLI>
                      <Content>
                        {t('privacy:additionalStates.secondBullet.firstSubBullet')}
                      </Content>
                    </LargeLI>
                    <LargeLI>
                      <Content>
                        {t('privacy:additionalStates.secondBullet.secondSubBullet')}
                      </Content>
                    </LargeLI>
                    <LargeLI>
                      <Content>
                        {t('privacy:additionalStates.secondBullet.thirdSubBullet')}
                      </Content>
                    </LargeLI>
                    <LargeLI>
                      <Content>
                        {t('privacy:additionalStates.secondBullet.fourthSubBullet')}
                      </Content>
                    </LargeLI>
                    <LargeLI>
                      <Content>
                        {t('privacy:additionalStates.secondBullet.fifthSubBullet')}
                      </Content>
                    </LargeLI>
                    <LargeLI>
                      <Content>
                        {t('privacy:additionalStates.secondBullet.sixthSubBullet')}
                      </Content>
                    </LargeLI>
                    <LargeLI>
                      <Content>{t('privacy:additionalStates.secondBullet.seventhSubBullet')}</Content>
                    </LargeLI>
                    <LargeLI>
                      <Content>{t('privacy:additionalStates.secondBullet.eighthSubBullet')}</Content>
                    </LargeLI>
                  </ul>
                </LargeLI>
                <LargeLI>
                  <Content>
                    {t('privacy:additionalStates.thirdBullet')}
                  </Content>
                </LargeLI>
                <LargeLI>
                  <Content>
                    {t('privacy:additionalStates.fourthBullet')}
                  </Content>
                </LargeLI>
                <LargeLI>
                  <Content>
                    {t('privacy:additionalStates.fifthBullet')}
                  </Content>
                </LargeLI>
                <LargeLI>
                  <Content>
                    {t('privacy:additionalStates.sixthBullet')}
                  </Content>
                </LargeLI>
              </ul>
              <ItalicSubHeading>{t('privacy:additionalStates.PersonalInformationSales.heading')}</ItalicSubHeading>
              <ul>
                <LargeLI>
                  <Content>
                    {t('privacy:additionalStates.PersonalInformationSales.firstBullet')}
                  </Content>
                </LargeLI>
                <LargeLI>
                  <Content>
                    {t('privacy:additionalStates.PersonalInformationSales.secondBullet')}
                  </Content>
                </LargeLI>
                <LargeLI>
                  <Content>
                    {t('privacy:additionalStates.PersonalInformationSales.thirdBullet')}
                  </Content>
                </LargeLI>
              </ul>
              <ItalicSubHeading>{t('privacy:additionalStates.dataProtectionRights.heading')}</ItalicSubHeading>
              <Content>
                {t('privacy:additionalStates.dataProtectionRights.firstParagraph')}
              </Content>
              <Content>
                {t('privacy:additionalStates.dataProtectionRights.secondParagraph')}
              </Content>
              <Content>{t('privacy:additionalStates.dataProtectionRights.thirdParagraph.heading')}</Content>
              <ul>
                <LargeLI>
                  <Content>
                    {t('privacy:additionalStates.dataProtectionRights.thirdParagraph.firstBullet')}
                  </Content>
                </LargeLI>
                <LargeLI>
                  <Content>
                    {t('privacy:additionalStates.dataProtectionRights.thirdParagraph.secondBullet')}
                  </Content>
                </LargeLI>
              </ul>
              <Content>
                {t('privacy:additionalStates.dataProtectionRights.fourthParagraph.heading')}
              </Content>
              <ul>
                <LargeLI>
                  <Content>{t('privacy:additionalStates.dataProtectionRights.fourthParagraph.firstBullet')}</Content>
                </LargeLI>
                <LargeLI>
                  <Content>{t('privacy:additionalStates.dataProtectionRights.fourthParagraph.secondBullet')}</Content>
                </LargeLI>
              </ul>
              <Content>
                {t('privacy:additionalStates.dataProtectionRights.fifthParagraph')}
              </Content>
              <Content>
                {t('privacy:additionalStates.dataProtectionRights.sixthParagraph')}
              </Content>
              <Content>
                {t('privacy:additionalStates.dataProtectionRights.seventhParagraph')}
              </Content>
              <Content>
                {t('privacy:additionalStates.dataProtectionRights.eighthParagraph')}
              </Content>
              <ItalicSubHeading>{t('privacy:additionalStates.californiaShine.heading')}</ItalicSubHeading>
              <Content>
                {t('privacy:additionalStates.californiaShine.firstParagraph')}
              </Content>
              <Content>
                {t('privacy:additionalStates.californiaShine.secondParagraph')}
              </Content>
              <ItalicSubHeading>{t('privacy:additionalStates.californiaMinors.heading')}</ItalicSubHeading>
              <Content>
                {t('privacy:additionalStates.californiaMinors.firstParagraph')}
              </Content>
              <Content>
                {t('privacy:additionalStates.californiaMinors.secondParagraph')}
              </Content>
              <Content>
                {t('privacy:additionalStates.californiaMinors.thirdParagraph')}
              </Content>
            </ContentSection>
          </Section>
        </ContentContainer>
      </Trans>
    </PageContainer>
  );
};
